/**
 * Prøvesett-veiviser med 6 steg + modus-velger.
 *
 * Fase 2: modus-velger + stegstruktur + data-state.
 * Fase 3: ekte steg-innhold via wizards/exam-set-steps/*.
 * Fase 4: inline rubric-opprettelse (modal-i-modal).
 * Fase 7: bildestøtte i spørsmål-steget.
 * Fase 8: smart randomisering per kurs (backend-change, UI uendret).
 * Fase 9: tittel-maler i kurs-veiviser (ikke her).
 */
import { useState, type ReactNode } from 'react';
import {
  Award, Save, Sparkles, FileText, ChevronRight, Rocket, BookOpen
} from 'lucide-react';
import WizardModal, { type WizardStepDef, type WizardFinalAction } from './WizardModal';
import { generateExamQuestions, createExamSet } from '../../lib/examApi';
import type {
  GradingScale, ExamQuestion, DifficultyDistribution, CreateExamRequest,
  CompositeComponentType, CompositeMode, CompositeTemplateComponent,
} from '../../types/exam';
import { useToast } from '../ui';
import BasicsStep from './exam-set-steps/BasicsStep';
import AssessmentStep from './exam-set-steps/AssessmentStep';
import RubricStep from './exam-set-steps/RubricStep';
import TechStep from './exam-set-steps/TechStep';
import QuestionsStep from './exam-set-steps/QuestionsStep';
import SaveStep from './exam-set-steps/SaveStep';
import InlineCreateRubricModal from './InlineCreateRubricModal';

type WizardMode = 'blank' | 'ai_scaffold';
type PrimaryAssessmentType = CompositeComponentType | 'other';

/**
 * CX kompositt-veiviser (2026-11-01):
 * Draft-objekt per komponent brukeren velger. Mappes til composite_template
 * ved innsending. _weight_user_edited er UI-only — forteller smart defaults
 * at vi ikke skal overstyre vekter brukeren allerede har endret.
 */
export interface WizardComponentDraft {
  component_type: CompositeComponentType;
  sequence: number;
  weight_pct: number | null;
  rubric_id: string | null;
  is_anonymous: boolean;
  grace_period_hours: number;
  max_files_per_submission: number;
  task_description: string;
  _weight_user_edited?: boolean;
}

/** Full form-state som samles opp gjennom veiviseren. Mapper 1:1 til CreateExamRequest + flagg. */
export interface ExamSetWizardData {
  // Steg 1 — Grunninfo
  study_plan_id: string;
  module_coverage: 'full' | 'subset';
  included_module_ids: string[];
  title: string;
  description: string;
  validity_years: number | null;

  // Steg 2 — Vurdering
  time_limit_minutes: number | null;
  pass_threshold: number;
  grading_scale: GradingScale;
  primary_assessment_type: PrimaryAssessmentType;

  // CX kompositt-veiviser: multi-select komponenter + modus
  components: WizardComponentDraft[];
  composite_mode: CompositeMode;

  // Steg 3 — Rubric + rammeverk (legacy; brukes for single-component)
  rubric_id: string | null;
  framework_rationale: string;

  // Steg 4 — Teknisk
  seb_required: boolean;
  use_question_bank: boolean;
  question_count: number;
  difficulty_distribution: DifficultyDistribution;

  // Steg 5 — Spørsmål (hvis skriftlig + statisk)
  questions: ExamQuestion[];
}

const INITIAL_DATA: ExamSetWizardData = {
  study_plan_id: '',
  module_coverage: 'full',
  included_module_ids: [],
  title: '',
  description: '',
  validity_years: null,

  time_limit_minutes: 60,
  pass_threshold: 60,
  grading_scale: 'pass_fail',
  primary_assessment_type: 'written',

  // CX default: single-komponent skriftlig (bakoverkompatibelt)
  components: [{
    component_type: 'written',
    sequence: 1,
    weight_pct: null,
    rubric_id: null,
    is_anonymous: true,
    grace_period_hours: 0,
    max_files_per_submission: 0,
    task_description: '',
  }],
  composite_mode: null,

  rubric_id: null,
  framework_rationale: '',

  // Kents nye defaults: SEB + spørsmålsbank er standard for kvalitet
  seb_required: true,
  use_question_bank: true,
  question_count: 20,
  difficulty_distribution: { easy: 0.2, medium: 0.6, hard: 0.2 },

  questions: [],
};

export interface NewExamSetWizardProps {
  open: boolean;
  onClose: () => void;
  /** Kalt når prøvesett er lagret. Returnerer exam_set_id slik kurs-veiviser kan trigges. */
  onComplete?: (examSetId: string) => void;
}

export default function NewExamSetWizard({ open, onClose, onComplete }: NewExamSetWizardProps) {
  const toast = useToast();
  const [mode, setMode] = useState<WizardMode | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<ExamSetWizardData>(INITIAL_DATA);
  const [hasChanges, setHasChanges] = useState(false);
  const [scaffoldLoading, setScaffoldLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [createRubricOpen, setCreateRubricOpen] = useState(false);
  // CX: når bruker klikker "Opprett ny matrise" på en spesifikk komponent, husker
  // vi index-en så vi kan tilordne nyopprettet rubric_id tilbake til den komponenten.
  const [createRubricForIdx, setCreateRubricForIdx] = useState<number | null>(null);
  // Bumpes etter opprettelse av ny matrise slik at RubricStep henter listen på nytt
  const [rubricRefreshKey, setRubricRefreshKey] = useState(0);

  const patch = (updates: Partial<ExamSetWizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const reset = () => {
    setMode(null);
    setCurrentStep(0);
    setData(INITIAL_DATA);
    setHasChanges(false);
    setScaffoldLoading(false);
    setSavingKey(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // KI-scaffold: kall generateExamQuestions og forhåndsutfyll felter
  const runAiScaffold = async () => {
    if (!data.study_plan_id) {
      toast.showWarning('Velg studieplan før du henter KI-forslag.');
      return;
    }
    setScaffoldLoading(true);
    try {
      const generated = await generateExamQuestions(data.study_plan_id);
      patch({
        title: generated.suggested_title,
        description: generated.suggested_description,
        time_limit_minutes: generated.suggested_time_limit_minutes,
        pass_threshold: generated.suggested_pass_threshold,
        grading_scale: generated.suggested_grading_scale,
        validity_years: generated.suggested_validity_years ?? null,
        questions: generated.questions,
        // KI-flyt setter default til statiske spørsmål siden den har generert
        use_question_bank: false,
      });
      toast.showSuccess(`KI-forslag mottatt — ${generated.questions.length} spørsmål foreslått.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ukjent feil';
      toast.showError('KI-forslag feilet: ' + msg);
    } finally {
      setScaffoldLoading(false);
    }
  };

  // Bygg CreateExamRequest fra wizard-data
  const buildRequest = (): CreateExamRequest => {
    // Komponent-liste → composite_template (kun hvis >1 komponent)
    const isComposite = data.components.length > 1;
    const compositeTemplate: CompositeTemplateComponent[] | null = isComposite
      ? data.components.map((c, i) => ({
          component_type: c.component_type,
          sequence: i + 1,
          weight_pct: data.composite_mode === 'weighted_composite' ? c.weight_pct : null,
          rubric_id: c.rubric_id,
          is_anonymous: c.is_anonymous,
          grace_period_hours: c.grace_period_hours,
          max_files_per_submission: c.max_files_per_submission,
          task_description: c.task_description.trim() || null,
        }))
      : null;

    // Primary = første komponent (backwards-kompat)
    const primary = data.components[0]?.component_type || 'written';
    // Single-component rubric: for ikke-written tar vi fra første komponent;
    // for written beholdes evt. legacy rubric_id (satt via RubricStep).
    const singleRubricId = isComposite ? null : (primary === 'written' ? data.rubric_id : data.components[0]?.rubric_id ?? null);

    return {
      study_plan_id: data.study_plan_id,
      title: data.title.trim(),
      description: data.description.trim() || undefined,
      time_limit_minutes: data.time_limit_minutes ?? undefined,
      pass_threshold: data.pass_threshold,
      grading_scale: data.grading_scale,
      validity_years: data.validity_years ?? undefined,
      seb_required: data.seb_required,
      questions: data.use_question_bank ? [] : data.questions,
      use_question_bank: data.use_question_bank,
      question_count: data.use_question_bank ? data.question_count : undefined,
      difficulty_distribution: data.use_question_bank ? data.difficulty_distribution : undefined,
      included_module_ids:
        data.module_coverage === 'subset' && data.included_module_ids.length > 0
          ? data.included_module_ids
          : null,
      primary_assessment_type: primary,
      framework_rationale: data.framework_rationale.trim() || undefined,
      has_rubric: Boolean(singleRubricId),
      rubric_id: singleRubricId,
      composite_mode: isComposite ? data.composite_mode : null,
      composite_template: compositeTemplate,
    };
  };

  const validate = (): string | null => {
    if (!data.study_plan_id) return 'Studieplan må velges (steg 1).';
    if (!data.title.trim()) return 'Tittel må settes (steg 1).';
    if (data.components.length === 0) return 'Minst én vurderingsform må velges (steg 2).';
    const hasWritten = data.components.some(c => c.component_type === 'written');
    if (hasWritten && !data.use_question_bank && data.questions.length === 0) {
      return 'Legg til minst ett spørsmål, eller aktiver spørsmålsbank (steg 4).';
    }
    // Kompositt-sjekk: vekter må summere 100 ved weighted
    if (data.components.length > 1) {
      if (!data.composite_mode) return 'Velg modus (vektet eller alle må bestås) for kompositt-vurdering (steg 2).';
      if (data.composite_mode === 'weighted_composite') {
        const sum = data.components.reduce((s, c) => s + (c.weight_pct || 0), 0);
        if (sum !== 100) return `Vektene må summere til 100 % (nå ${sum} %) — juster i steg 2.`;
      }
      // Rubric-tvang for ikke-written (Trinn C-sjekk, legges inn her for å blokkere i validate)
      const missing = data.components.filter(c => c.component_type !== 'written' && !c.rubric_id);
      if (missing.length > 0) {
        return `Vurderingsmatrise mangler for: ${missing.map(c => c.component_type).join(', ')} — velg i steg 3 (Matrise).`;
      }
    }
    return null;
  };

  const save = async (actionKey: string): Promise<string | null> => {
    const err = validate();
    if (err) {
      toast.showWarning(err);
      return null;
    }
    setSavingKey(actionKey);
    try {
      const examSet = await createExamSet(buildRequest());
      toast.showSuccess('Prøvesett opprettet!');
      return examSet.id;
    } catch (err_: unknown) {
      const msg = err_ instanceof Error ? err_.message : 'Ukjent feil';
      toast.showError('Lagring feilet: ' + msg);
      return null;
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveDraft = async () => {
    // "Utkast" lagres fortsatt via createExamSet (backend tar vare på published-flagg).
    // Fase 3+ kan skille mellom draft og published hvis backend får en flagg-param.
    const id = await save('save-draft');
    if (id) {
      reset();
      onClose();
    }
  };
  const handleSavePublish = async () => {
    const id = await save('save-publish');
    if (id) {
      // Publiser er ferdig — IKKE åpne kurs-veiviser. onComplete kalles kun i "og start kurs-veiviser".
      reset();
      onClose();
    }
  };
  const handleSaveAndCourse = async () => {
    const id = await save('save-and-course');
    if (id) {
      reset();
      onClose();
      onComplete?.(id); // Parent (CoursesAndExams) åpner kurs-veiviseren
    }
  };

  // ====== Modus-velger (vises før steg 1) ======
  if (open && mode === null) {
    return (
      <ModeChooser
        onPickBlank={() => setMode('blank')}
        onPickAiScaffold={() => setMode('ai_scaffold')}
        onCancel={handleClose}
      />
    );
  }

  if (!open) return null;

  // ====== Steg-definisjoner (med ekte innhold) ======
  const nonWritten = data.primary_assessment_type !== 'written';

  const steps: WizardStepDef[] = [
    {
      key: 'basics',
      label: 'Grunninfo',
      description: 'Studieplan, omfang og tittel',
      content: (
        <BasicsStep
          data={data}
          patch={patch}
          aiScaffoldMode={mode === 'ai_scaffold'}
          scaffoldLoading={scaffoldLoading}
          onTriggerScaffold={runAiScaffold}
        />
      ),
      canAdvance:
        !data.study_plan_id
          ? 'Du må velge en studieplan.'
          : !data.title.trim()
          ? 'Tittel må fylles ut.'
          : data.module_coverage === 'subset' && data.included_module_ids.length === 0
          ? 'Du valgte "utvalg moduler" — velg minst én modul, eller bytt til "hele studieplanen".'
          : true,
    },
    {
      key: 'assessment',
      label: 'Vurdering',
      description: 'Varighet, beståttgrense, skala og vurderingsform',
      content: <AssessmentStep data={data} patch={patch} />,
    },
    {
      key: 'rubric',
      label: 'Matrise',
      description: 'Vurderingsmatrise + rammeverk-begrunnelse',
      content: (
        <RubricStep
          key={`rubric-${rubricRefreshKey}`}
          data={data}
          patch={patch}
          onOpenCreateRubric={(idx?: number) => {
            setCreateRubricForIdx(idx ?? null);
            setCreateRubricOpen(true);
          }}
        />
      ),
    },
    {
      key: 'tech',
      label: 'Teknisk',
      description: 'Safe Exam Browser + spørsmålskilde',
      content: <TechStep data={data} patch={patch} />,
    },
    {
      key: 'questions',
      label: 'Spørsmål',
      description: nonWritten ? 'Hoppes over for ikke-skriftlig vurdering' : 'Bank eller statiske spørsmål',
      content: <QuestionsStep data={data} patch={patch} />,
    },
    {
      key: 'save',
      label: 'Lagring',
      description: 'Oppsummering og lagringshandling',
      content: <SaveStep data={data} />,
    },
  ];

  const canSave = !validate();
  const finalActions: WizardFinalAction[] = [
    {
      key: 'save-draft',
      label: 'Lagre som utkast',
      variant: 'secondary',
      icon: <FileText className="w-4 h-4" />,
      onClick: handleSaveDraft,
      loading: savingKey === 'save-draft',
      disabled: !canSave || savingKey !== null,
      disabledReason: validate() ?? undefined,
    },
    {
      key: 'save-publish',
      label: 'Lagre og publiser',
      variant: 'primary',
      icon: <Save className="w-4 h-4" />,
      onClick: handleSavePublish,
      loading: savingKey === 'save-publish',
      disabled: !canSave || savingKey !== null,
      disabledReason: validate() ?? undefined,
    },
    {
      key: 'save-and-course',
      label: 'Lagre og start kurs-veiviser',
      variant: 'success',
      icon: <Rocket className="w-4 h-4" />,
      onClick: handleSaveAndCourse,
      loading: savingKey === 'save-and-course',
      disabled: !canSave || savingKey !== null,
      disabledReason: validate() ?? undefined,
    },
  ];

  // Default component type for inline rubric create: hvis bruker klikket "opprett"
  // på en spesifikk komponent-rad bruker vi den typen, ellers primær-vurderingsform.
  const defaultRubricComponent: 'written' | 'oral' | 'practical' | 'observation' = (() => {
    const t = createRubricForIdx !== null
      ? data.components[createRubricForIdx]?.component_type
      : data.primary_assessment_type;
    if (t === 'oral') return 'oral';
    if (t === 'practical') return 'practical';
    if (t === 'observation') return 'observation';
    return 'written';
  })();

  return (
    <>
      <WizardModal
        open={open}
        onClose={handleClose}
        title="Nytt prøvesett"
        subtitle={
          mode === 'ai_scaffold'
            ? 'KI-forslag fyller inn forslag etter du har valgt studieplan'
            : 'Start fra bunnen og fyll ut selv'
        }
        icon={
          mode === 'ai_scaffold' ? (
            <Sparkles className="w-4 h-4 text-kf-orange-500" />
          ) : (
            <Award className="w-4 h-4 text-kf-orange-500" />
          )
        }
        steps={steps}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        finalActions={finalActions}
        hasChanges={hasChanges}
      />

      <InlineCreateRubricModal
        open={createRubricOpen}
        onClose={() => {
          setCreateRubricOpen(false);
          setCreateRubricForIdx(null);
        }}
        defaultComponentType={defaultRubricComponent}
        onCreated={(rubricId) => {
          // Tilordne rubric til spesifikk komponent hvis bruker klikket "opprett"
          // på en bestemt rad. Ellers behold legacy-oppførsel (single-rubric).
          if (createRubricForIdx !== null && data.components[createRubricForIdx]) {
            const next = data.components.map((c, i) =>
              i === createRubricForIdx ? { ...c, rubric_id: rubricId } : c
            );
            patch({ components: next });
          } else {
            patch({ rubric_id: rubricId });
          }
          setRubricRefreshKey((k) => k + 1);
        }}
      />
    </>
  );
}

// ===== Hjelpekomponenter =====

interface ModeChooserProps {
  onPickBlank: () => void;
  onPickAiScaffold: () => void;
  onCancel: () => void;
}

function ModeChooser({ onPickBlank, onPickAiScaffold, onCancel }: ModeChooserProps) {
  return (
    <div
      className="fixed inset-0 z-[8500] flex items-stretch sm:items-center justify-center sm:p-4 bg-kf-gray-900/60 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-2xl flex flex-col h-full sm:h-auto">
        <header className="px-4 sm:px-6 py-4 border-b border-kf-gray-200 bg-gradient-to-r from-kf-blue-50 to-kf-orange-50/50 sm:rounded-t-xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-kf-blue-700 font-semibold mb-1">
            <Award className="w-4 h-4 text-kf-orange-500" />
            Veiviser · Oppstart
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-kf-gray-900">Hvordan vil du starte?</h2>
          <p className="text-sm text-kf-gray-600 mt-1">
            Veiviseren tar deg steg for steg gjennom opprettelsen. Du kan starte fra bunnen eller få et KI-forslag som
            fyller inn tittel, beskrivelse, vurdering og spørsmål — som du kan justere.
          </p>
        </header>
        <div className="px-4 sm:px-6 py-5 flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ModeCard
              icon={<BookOpen className="w-6 h-6 text-kf-blue-600" />}
              title="Start fra bunnen"
              description="Fyll ut selv. Du har full kontroll over alle felter."
              onClick={onPickBlank}
            />
            <ModeCard
              icon={<Sparkles className="w-6 h-6 text-kf-orange-500" />}
              title="Få utkast fra systemet med KI"
              description="Velg studieplan, så fyller systemet inn tittel, beskrivelse, vurderingsvalg og foreslår spørsmål."
              onClick={onPickAiScaffold}
              accent
            />
          </div>
        </div>
        <footer className="px-4 sm:px-6 py-3 border-t border-kf-gray-200 bg-kf-gray-50 sm:rounded-b-xl flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-kf-gray-700 hover:text-kf-gray-900 hover:bg-white rounded transition-colors"
          >
            Avbryt
          </button>
        </footer>
      </div>
    </div>
  );
}

interface ModeCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  accent?: boolean;
}

function ModeCard({ icon, title, description, onClick, accent }: ModeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 rounded-lg border transition-all hover:shadow-md hover:-translate-y-0.5 ${
        accent
          ? 'border-kf-orange-300 bg-gradient-to-br from-kf-orange-50 to-white hover:border-kf-orange-400'
          : 'border-kf-gray-200 bg-white hover:border-kf-blue-300'
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-white border border-kf-gray-200 flex items-center justify-center">
          {icon}
        </div>
        <h3 className="font-semibold text-kf-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-kf-gray-600">{description}</p>
      <div className="mt-3 flex items-center gap-1 text-sm font-medium text-kf-blue-600">
        Velg
        <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
}
